import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { acars, ofpApi, gates as gatesApi, weather, dispatch, live as liveApi } from "@/lib/api";
import { withFlightFooter } from "@/lib/flight-footer";
import {
  Loader2, Cloud, MapPin, PlaneTakeoff, PlaneLanding, ExternalLink, Search,
  Wind, Thermometer, Fuel, MessageSquare, RefreshCw, Send, FileText, X,
} from "lucide-react";
import { toast } from "sonner";

/**
 * Flight Plan
 * -----------
 * All about the pilot's currently INITed flight:
 *   1. METAR / TAF lookup — always available, any airport.
 *   2. Gate assignment — FlightAware (North America) or Aviation Stack → manual override → "Contact Dispatcher".
 *   3. OFP briefing — pulled from SimBrief and matched by orig/dest before
 *      it is shown. It really is now; the comment said so for a while first.
 *
 * When there is no active flight, the METAR lookup still works and a friendly
 * prompt points pilots to Init Flight.
 */
export default function Fpls() {
  const { data: active } = useQuery({
    queryKey: ["acars-active"],
    queryFn: acars.active,
    refetchInterval: 8000,
  });

  return (
    <div className="p-8 max-w-[1400px]" data-testid="fpls-page">
      <div className="mb-6">
        <div className="overline mb-1">Briefing</div>
        <h1 className="text-4xl font-bold tracking-tight">Flight Plan</h1>
        <div className="acbar" />
        <p className="text-sm text-muted-foreground mt-4 max-w-2xl">
          METAR / TAF for any airport, live gate assignment, and the full OFP
          for your active flight.
        </p>
      </div>

      <MetarLookup active={active} />

      {!active ? (
        <div className="card-flat p-10 text-center mt-6" data-testid="fpls-empty">
          <div className="text-lg font-bold mb-1">No active flight</div>
          <p className="text-sm text-muted-foreground mb-4">
            Head over to <Link to="/init" className="text-primary hover:underline">Init Flight</Link> to
            file one of your bidded flights. Gate info and the OFP briefing will
            appear here once you&apos;re INIT&apos;d.
          </p>
        </div>
      ) : (
        <>
          <ActiveFlightBanner active={active} />
          <GateSection active={active} />
          <OfpSection active={active} />
        </>
      )}
    </div>
  );
}

// ============================ Active flight banner ===========================
function ActiveFlightBanner({ active }) {
  return (
    <section className="card-flat mb-6 border-primary" data-testid="fpls-active-banner">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="live-dot" />
          <div>
            <div className="overline text-primary">Active flight</div>
            <div className="text-lg font-bold font-mono">
              {active.flt} · {active.orig} → {active.dest}
            </div>
          </div>
        </div>
        <Link
          to="/tracking"
          data-testid="fpls-open-tracking"
          className="bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest rounded-md px-4 py-2 hover:brightness-110 flex items-center gap-2"
        >
          Open live tracking
        </Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 text-sm">
        <Kv label="Type"     v={active.type} />
        <Kv label="Reg"      v={active.reg || "—"} border />
        <Kv label="Pax (assigned)" v={active.planned_pax ?? "—"} border />
        <Kv label="Operator" v={active.operator || "—"} border />
        <Kv label="Fuel"     v={`${active.fuel ?? 0} kg`} border />
        <Kv label="Phase"    v={active.phase || "—"} border />
      </div>
    </section>
  );
}

// ============================ METAR Lookup ===================================
function MetarLookup({ active }) {
  const [input, setInput] = useState("");
  const [queryIcao, setQueryIcao] = useState("");

  const { data, isLoading, error } = useQuery({
    queryKey: ["metar", queryIcao],
    queryFn: () => weather.metar(queryIcao),
    enabled: !!queryIcao,
    staleTime: 60_000,
  });

  // Who is controlling this field on VATSIM, and the current ATIS. Costs no
  // extra call to VATSIM — the controllers and ATIS arrive in the same
  // datafeed the presence badges already read, and were being parsed away.
  // 60 s to match the interval the feed itself asks for.
  const { data: atc } = useQuery({
    queryKey: ["vatsim-atc", queryIcao],
    queryFn: () => liveApi.atc(queryIcao),
    enabled: !!queryIcao,
    refetchInterval: 60_000,
    staleTime: 55_000,
  });

  const submit = (e) => {
    e.preventDefault();
    if (input.trim()) setQueryIcao(input.toUpperCase().trim());
  };

  const quickLoad = (icao) => {
    setInput(icao);
    setQueryIcao(icao);
  };

  return (
    <section className="card-flat mb-6" data-testid="fpls-metar">
      <div className="px-6 py-4 border-b border-border flex items-center gap-3">
        <Cloud className="w-5 h-5 text-primary" />
        <div>
          <div className="overline">Weather</div>
          <h2 className="text-lg font-bold">METAR / TAF lookup</h2>
        </div>
      </div>

      <form onSubmit={submit} className="p-6 flex flex-wrap gap-3 items-end border-b border-border">
        <label className="flex-1 block min-w-[240px]">
          <span className="overline block mb-1.5">Airport ICAO (comma-separated for multiple)</span>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value.toUpperCase())}
            placeholder="CYYZ, CYVR, KJFK"
            data-testid="fpls-metar-input"
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm font-mono text-foreground focus:outline-none focus:border-primary"
          />
        </label>
        <button
          type="submit"
          disabled={!input.trim()}
          data-testid="fpls-metar-submit"
          className="h-10 bg-primary text-primary-foreground text-sm font-bold uppercase tracking-widest rounded-md px-5 hover:brightness-110 disabled:opacity-60 flex items-center gap-2"
        >
          <Search className="w-4 h-4" /> Fetch
        </button>
        {active && (
          <div className="flex items-center gap-2 ml-2">
            <span className="overline">Quick:</span>
            <button type="button" onClick={() => quickLoad(active.orig)} data-testid="fpls-metar-quick-orig"
                    className="text-[11px] bg-secondary rounded-md px-2 py-1 hover:bg-primary hover:text-primary-foreground font-mono">
              {active.orig}
            </button>
            <button type="button" onClick={() => quickLoad(active.dest)} data-testid="fpls-metar-quick-dest"
                    className="text-[11px] bg-secondary rounded-md px-2 py-1 hover:bg-primary hover:text-primary-foreground font-mono">
              {active.dest}
            </button>
            <button type="button" onClick={() => quickLoad(`${active.orig},${active.dest}`)}
                    className="text-[11px] bg-secondary rounded-md px-2 py-1 hover:bg-primary hover:text-primary-foreground font-mono">
              Both
            </button>
          </div>
        )}
      </form>

      <div className="p-6">
        {isLoading && (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Fetching METAR / TAF…
          </div>
        )}
        {error && (
          <div className="text-sm text-primary">
            {error.response?.data?.detail || "Weather service error"}
          </div>
        )}
        {!isLoading && !error && !queryIcao && (
          <div className="text-sm text-muted-foreground">
            Enter one or more ICAO codes above and hit Fetch.
          </div>
        )}
        {data && data.length === 0 && (
          <div className="text-sm text-muted-foreground">No observations found.</div>
        )}
        <div className="space-y-4">
          {(data || []).map((m) => (
            <div key={m.icaoId + m.obsTime} className="border border-border rounded-md p-4" data-testid={`metar-${m.icaoId}`}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-lg font-bold font-mono">{m.icaoId}</div>
                  <div className="text-xs text-muted-foreground font-mono">
                    Observed {new Date(m.obsTime * 1000).toUTCString()}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-sm font-mono">
                  {m.temp != null && (
                    <div className="flex items-center gap-1">
                      <Thermometer className="w-3 h-3 text-primary" />
                      {m.temp}°C
                    </div>
                  )}
                  {m.wdir != null && m.wspd != null && (
                    <div className="flex items-center gap-1">
                      <Wind className="w-3 h-3 text-primary" />
                      {m.wdir}° / {m.wspd}kt{m.wgst ? `G${m.wgst}` : ""}
                    </div>
                  )}
                  {/* aviationweather.gov reports `altim` in hPa for every
                      station regardless of how the METAR itself is written —
                      CYYZ comes back as 1032.3 while its raw observation says
                      A3048. Canada and the US set altimeters in inches, so
                      convert; 1 inHg is 33.8639 hPa exactly, and two decimals
                      reproduces the A-group in the raw METAR. */}
                  {m.altim != null && (
                    <div title={`${Math.round(m.altim)} hPa`}>
                      {(Number(m.altim) / 33.8639).toFixed(2)} inHg
                    </div>
                  )}
                </div>
              </div>
              <div className="mb-2">
                <div className="overline mb-1">METAR</div>
                <div className="text-sm font-mono break-words">{m.rawOb}</div>
              </div>
              {m.rawTaf && (
                <div>
                  <div className="overline mb-1">TAF</div>
                  <div className="text-sm font-mono break-words">{m.rawTaf}</div>
                </div>
              )}

              <AtcBlock icao={m.icaoId} atc={atc} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * VATSIM coverage for one field.
 *
 * Three states, and they must not collapse into each other:
 *   feed unreadable  -> say so. A pilot told "no ATC at your destination"
 *                       because WE could not reach VATSIM would plan around a
 *                       fact we invented.
 *   nobody online    -> "uncontrolled", which is real and actionable.
 *   positions        -> tower above ground, with frequencies.
 *
 * Airport positions only. Enroute sectors are excluded server-side: their
 * callsign prefix is a FIR identifier, not an airport.
 */
function AtcBlock({ icao, atc }) {
  if (!atc) return null;
  const feedDown = atc.feed === "unavailable";
  const here = atc.airports?.[icao];
  const positions = here?.positions || [];
  // A big field publishes separate arrival and departure ATIS (EGLL_A_ATIS and
  // EGLL_D_ATIS). Showing only the first would hand the departure ATIS to
  // someone arriving, which is worse than showing none. atis_all keeps both;
  // `atis` is the single-entry fallback for fields that publish one.
  const atisAll = here?.atis_all?.length ? here.atis_all : (here?.atis ? [here.atis] : []);

  return (
    <div className="mt-4 pt-4 border-t border-border" data-testid={`atc-${icao}`}>
      <div className="overline mb-2">VATSIM</div>

      {feedDown ? (
        <div className="text-xs text-muted-foreground" data-testid={`atc-${icao}-unknown`}>
          Datafeed unavailable — coverage unknown, not necessarily uncontrolled.
        </div>
      ) : positions.length === 0 && atisAll.length === 0 ? (
        <div className="text-xs text-muted-foreground" data-testid={`atc-${icao}-none`}>
          Uncontrolled — no positions online.
        </div>
      ) : (
        <>
          {positions.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2" data-testid={`atc-${icao}-positions`}>
              {positions.map((p) => (
                <span key={p.callsign}
                      className="inline-flex items-center gap-2 px-2 py-1 rounded-sm border border-primary/40 bg-primary/10 text-[11px] font-mono">
                  <span className="font-semibold">{p.facility}</span>
                  <span className="text-muted-foreground">{p.frequency}</span>
                </span>
              ))}
            </div>
          )}
          {atisAll.map((a) => {
            // EGLL_A_ATIS -> "Arrival", EGLL_D_ATIS -> "Departure", plain
            // EGLL_ATIS -> no qualifier. Read off the callsign because that is
            // where VATSIM puts it.
            const part = String(a.callsign || "").split("_");
            const kind = part.length > 2
              ? ({ A: "Arrival", D: "Departure" }[part[1]] || part[1])
              : null;
            return (
              <div key={a.callsign} className="text-xs mb-2 last:mb-0"
                   data-testid={`atc-${icao}-atis`}>
                <span className="font-mono font-semibold text-primary">
                  {kind ? `${kind} ATIS` : "ATIS"} {a.code || "—"}
                </span>
                {a.frequency && (
                  <span className="font-mono text-muted-foreground"> · {a.frequency}</span>
                )}
                {a.text && (
                  <div className="mt-1 font-mono text-[11px] text-muted-foreground break-words">
                    {a.text}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

// ============================ OFP for the active flight ======================
/** Does this OFP describe the flight the pilot is actually on?
 *
 * SimBrief plans are per-pilot, not per-flight, so `/ofp/latest` answers with
 * whatever the pilot generated most recently — which may be a completely
 * different route. `/ofp/for-bid` already checks this server-side and 409s on a
 * mismatch, but the check is repeated here because a pinned ofp_id bypasses it,
 * and because a briefing for the wrong flight must never render silently.
 *
 * Compares the last three characters: the assignments table stores IATA (YYZ)
 * while SimBrief returns ICAO (CYYZ). Same comparison `_match()` makes in ofp.py.
 */
export function ofpMatchesFlight(ofp, active) {
  if (!ofp || !active) return { ok: true };
  const tail3 = (v) => String(v || "").toUpperCase().slice(-3);
  const want = { orig: tail3(active.orig), dest: tail3(active.dest) };
  const got = { orig: tail3(ofp.origin?.icao), dest: tail3(ofp.destination?.icao) };
  if (!want.orig || !want.dest || !got.orig || !got.dest) return { ok: true };
  return { ok: want.orig === got.orig && want.dest === got.dest, want, got };
}

/** The OFP for the flight in progress, and whether it is really for it.
 *
 * Shared by the briefing and the dispatch-message footer so the two cannot
 * disagree, and so a gate request never quotes another flight's fuel figures.
 *
 * for-bid when the bid is known — that endpoint validates the route and caches
 * the ofp_id against it. `latest` is the fallback for a flight initialised
 * before migration 007 stored assignment_id, and it is the endpoint that caused
 * this bug, so whatever it returns is still matched before use.
 */
function useActiveOfp(active) {
  const bidId = active?.assignment_id;
  const q = useQuery({
    queryKey: ["ofp-active", active?.id, bidId ?? "latest"],
    queryFn: () => (bidId ? ofpApi.forBid(bidId) : ofpApi.latest()),
    retry: false,
    staleTime: 60_000,
    enabled: !!active?.id,
    // A pilot who opened this page before generating the plan saw "no plan"
    // until they found Reload: nothing asked SimBrief again. Now it asks when
    // they come back to the window (from simbrief.com, usually), and once a
    // minute for as long as there is no plan for this flight. A good plan
    // stops the polling.
    refetchOnWindowFocus: true,
    refetchInterval: (q) =>
      q.state.status === "error" || !ofpMatchesFlight(q.state.data, active).ok || !q.state.data
        ? 60_000
        : false,
  });
  const match = ofpMatchesFlight(q.data, active);
  return { ...q, match, ofp: match.ok ? q.data : null };
}

// ============================ Gate section ===================================
function GateSection({ active }) {
  const qc = useQueryClient();
  const nav_ = useNavigate();
  const flightId = `${active.opr || active.operator || "AC"}${active.flt}`;
  const {
    data: live,
    isFetching: liveLoading,
    refetch: refetchLive,
  } = useQuery({
    queryKey: ["gate-active", flightId, active.orig, active.dest],
    queryFn: () => gatesApi.lookup(flightId, active.orig, active.dest),
    retry: false,
    staleTime: 60_000,
  });
  // `ofp` is null unless the plan is genuinely for this flight — a gate request
  // must not quote another flight's route, fuel or times to a dispatcher.
  const { ofp } = useActiveOfp(active);

  const [showDispatch, setShowDispatch] = useState(false);
  const [dispatchBody, setDispatchBody] = useState("");

  const createDispatch = useMutation({
    mutationFn: () => {
      const pilotMsg = dispatchBody.trim() || `Hi dispatch — I need a gate assignment for ${flightId} (${active.orig}→${active.dest}).`;
      return dispatch.create({
        subject: `Gate request · ${flightId} ${active.orig}→${active.dest}`,
        body: withFlightFooter(pilotMsg, active, ofp),
        flight: flightId,
        orig: active.orig,
        dest: active.dest,
        request_type: "GATE_REQ",
      });
    },
    onSuccess: () => {
      toast.success("Message sent to dispatch");
      setShowDispatch(false);
      setDispatchBody("");
      qc.invalidateQueries({ queryKey: ["dispatch-threads"] });
      qc.invalidateQueries({ queryKey: ["dispatch-unread"] });
      nav_("/dispatch");
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Failed"),
  });

  const effectiveDep = live?.gate_dep || active.gateOrig || null;
  const effectiveArr = live?.gate_arr || active.gateDest || null;
  const noGates = !effectiveDep && !effectiveArr;

  return (
    <section className="card-flat mb-6" data-testid="fpls-gate">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MapPin className="w-5 h-5 text-primary" />
          <div>
            <div className="overline">Ground ops</div>
            <h2 className="text-lg font-bold">Gate assignment</h2>
          </div>
        </div>
        <button
          onClick={() => refetchLive()}
          disabled={liveLoading}
          data-testid="fpls-gate-refresh"
          className="text-xs bg-secondary rounded-md px-3 py-2 hover:bg-primary hover:text-primary-foreground flex items-center gap-1 font-bold uppercase tracking-widest"
        >
          {liveLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
          Refresh
        </button>
      </div>
      <div className="p-6 grid md:grid-cols-2 gap-4">
        <GateTile
          testid="fpls-gate-dep"
          icon={PlaneTakeoff}
          icao={active.orig}
          label="Departure"
          gate={effectiveDep}
          terminal={live?.terminal_dep}
          source={live?.gate_dep ? live.source_name : active.gateOrig ? "Manual override" : null}
        />
        <GateTile
          testid="fpls-gate-arr"
          icon={PlaneLanding}
          icao={active.dest}
          label="Arrival"
          gate={effectiveArr}
          terminal={live?.terminal_arr}
          source={live?.gate_arr ? live.source_name : active.gateDest ? "Manual override" : null}
        />
      </div>
      {live?.status && (
        <div className="px-6 pb-3 text-[11px] text-muted-foreground font-mono">
          Flight status: {String(live.status).toUpperCase()}
        </div>
      )}

      {noGates && !showDispatch && (
        <div className="px-6 py-4 border-t border-border flex items-center gap-3 flex-wrap" data-testid="fpls-no-gate-cta">
          <span className="text-xs text-muted-foreground">
            No gate published yet for this flight.
          </span>
          <button
            onClick={() => setShowDispatch(true)}
            data-testid="fpls-contact-dispatcher"
            className="text-[11px] bg-primary text-primary-foreground rounded-md px-3 py-1.5 hover:brightness-110 flex items-center gap-1 font-bold uppercase tracking-widest"
          >
            <MessageSquare className="w-3 h-3" /> Contact dispatcher
          </button>
        </div>
      )}
      {showDispatch && (
        <div className="px-6 py-4 border-t border-border" data-testid="fpls-dispatch-form">
          <div className="overline mb-2">Message to dispatch</div>
          <textarea
            value={dispatchBody}
            onChange={(e) => setDispatchBody(e.target.value)}
            rows={3}
            placeholder={`Hi dispatch — I need a gate assignment for ${flightId} (${active.orig}→${active.dest}).`}
            data-testid="fpls-dispatch-body"
            className="w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-foreground focus:outline-none focus:border-primary"
          />
          <div className="flex justify-end gap-2 mt-2">
            <button
              onClick={() => { setShowDispatch(false); setDispatchBody(""); }}
              className="text-[11px] border border-border rounded-md px-3 py-1.5 text-muted-foreground hover:text-primary hover:border-primary font-bold uppercase tracking-widest"
            >
              Cancel
            </button>
            <button
              onClick={() => createDispatch.mutate()}
              disabled={createDispatch.isPending}
              data-testid="fpls-dispatch-send"
              className="text-[11px] bg-primary text-primary-foreground rounded-md px-3 py-1.5 hover:brightness-110 disabled:opacity-60 flex items-center gap-1 font-bold uppercase tracking-widest"
            >
              {createDispatch.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              Send
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function GateTile({ testid, icon: Icon, icao, label, gate, terminal, source }) {
  return (
    <div className="border border-border rounded-md p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${gate ? "text-primary" : "text-muted-foreground"}`} />
        <span className="overline">{label}</span>
      </div>
      <div className="flex items-baseline gap-3 flex-wrap">
        <span className="text-2xl font-mono font-bold">{icao}</span>
        <span className="text-sm text-muted-foreground">Gate</span>
        <span
          className="text-2xl font-mono font-bold text-primary"
          data-testid={`${testid}-value`}
        >
          {gate || "—"}
        </span>
        {terminal && (
          <span className="text-xs font-mono text-muted-foreground">T{terminal}</span>
        )}
      </div>
      {source && (
        <div className="text-[10px] text-muted-foreground uppercase tracking-widest font-mono mt-2">
          {source}
        </div>
      )}
    </div>
  );
}

// ============================ OFP section ====================================
function OfpSection({ active }) {
  const { data, isLoading, error, refetch, isFetching, match } = useActiveOfp(active);

  // Dismissal is keyed on the plan, not a bare boolean: a pilot who acknowledges
  // one mismatched OFP should still be warned when a DIFFERENT one loads. Reload
  // or a fresh SimBrief plan brings the banner back.
  const [dismissed, setDismissed] = useState(null);
  const ofpKey = data ? (data.ofp_id || `${data.origin?.icao}-${data.destination?.icao}`) : null;
  const warn = data && !match.ok && dismissed !== ofpKey;

  return (
    <section className="card-flat" data-testid="fpls-ofp">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="w-5 h-5 text-primary" />
          <div>
            <div className="overline">Briefing</div>
            <h2 className="text-lg font-bold">Operational Flight Plan</h2>
          </div>
        </div>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          data-testid="fpls-ofp-refresh"
          className="text-xs bg-secondary rounded-md px-3 py-2 hover:bg-primary hover:text-primary-foreground flex items-center gap-1 font-bold uppercase tracking-widest"
        >
          {isFetching ? <Loader2 className="w-3 h-3 animate-spin" /> : "Reload"}
        </button>
      </div>
      <div className="p-6">
        {(isLoading || isFetching) && (
          <div className="text-sm text-muted-foreground flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Fetching OFP from SimBrief…
          </div>
        )}
        {error && (
          <div className="text-sm text-primary">
            {error.response?.data?.detail || "Failed to load OFP"}
            <div className="text-xs text-muted-foreground mt-1">
              Confirm your <Link to="/settings" className="text-primary hover:underline">SimBrief Pilot ID</Link> is set and that you&apos;ve generated an OFP for this flight on simbrief.com.
            </div>
          </div>
        )}
        {warn && (
          <div
            className="text-sm border border-primary/60 bg-primary/10 px-4 py-3 rounded-md mb-4 flex items-start gap-3"
            data-testid="fpls-ofp-mismatch"
          >
            <div className="flex-1">
              <div className="font-semibold text-primary mb-1">This plan is for a different flight</div>
              <div className="text-xs text-muted-foreground font-mono">
                Your SimBrief plan is {match.got.orig} → {match.got.dest}; you are
                flying {match.want.orig} → {match.want.dest}.
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                The briefing below is shown as-is. If that is not what you meant to
                fly, generate an OFP for this route on simbrief.com and hit Reload.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setDismissed(ofpKey)}
              data-testid="fpls-ofp-mismatch-dismiss"
              aria-label="Dismiss route mismatch warning"
              className="shrink-0 text-muted-foreground hover:text-foreground transition-colors p-1 -m-1"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {data && <OfpBrief ofp={data} assignedPax={active?.planned_pax} />}
      </div>
    </section>
  );
}

function OfpBrief({ ofp, assignedPax }) {
  // The load myvac assigned at bid time is the one filed on the PIREP. A plan
  // made on simbrief.com directly, rather than from myvac's flight plan page
  // (which sends the assigned load), carries SimBrief's own figure, so the two
  // disagree and the fuel and weights below are for a different payload.
  const sbPax = ofp.weights?.pax_count;
  const paxDiffers = assignedPax != null && sbPax != null && Number(sbPax) !== Number(assignedPax);
  // Resolved server-side now. This used to build the URL here as
  // "…/ofp/flightplans/pdf/<name>", inventing a `pdf/` segment SimBrief does
  // not have — the portal joins <files><directory> to <files><pdf><link> with
  // nothing between them (fpls.php:790), which is why that button always 404'd.
  const pdfUrl = ofp.files?.pdf_url || null;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">Callsign</div>
          <div className="text-lg font-bold font-mono">
            {ofp.callsign} · {ofp.origin?.icao} → {ofp.destination?.icao}
          </div>
        </div>
        {pdfUrl && (
          <a href={pdfUrl} target="_blank" rel="noreferrer" className="text-xs bg-secondary rounded-md px-3 py-2 hover:bg-primary hover:text-primary-foreground flex items-center gap-1 font-bold uppercase tracking-widest">
            <ExternalLink className="w-3 h-3" /> Full PDF
          </a>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Info label="Aircraft"  v={`${ofp.aircraft?.icao || "—"} · ${ofp.aircraft?.reg || ""}`} />
        <Info label="Cruise"    v={ofp.initial_alt ? `FL${ofp.initial_alt/100}` : "—"} />
        <Info label="Cost idx"  v={ofp.cost_index || "—"} />
        <Info label="Enroute"   v={ofp.times?.est_time_enroute ? fmtSec(ofp.times.est_time_enroute) : "—"} />
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <AirportPanel icon={PlaneTakeoff} label="Departure" a={ofp.origin} />
        <AirportPanel icon={PlaneLanding} label="Arrival"   a={ofp.destination} />
      </div>
      <div className="border border-border rounded-md p-4">
        <div className="overline mb-2">Route</div>
        <div className="text-sm font-mono break-words">
          <span className="text-primary">{ofp.origin?.icao}</span>{" "}
          {ofp.route || "—"}{" "}
          <span className="text-primary">{ofp.destination?.icao}</span>
        </div>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div className="border border-border rounded-md p-4">
          <div className="overline mb-2 flex items-center gap-2"><Fuel className="w-3 h-3" /> Fuel ({ofp.fuel?.units || "kgs"})</div>
          <div className="grid grid-cols-2 gap-y-1 text-sm font-mono">
            <FuelLine k="Taxi"           v={ofp.fuel?.taxi} />
            <FuelLine k="Enroute"        v={ofp.fuel?.enroute} />
            <FuelLine k="Contingency"    v={ofp.fuel?.contingency} />
            <FuelLine k="Alternate"      v={ofp.fuel?.alternate} />
            <FuelLine k="Reserve"        v={ofp.fuel?.reserve} />
            <FuelLine k="Plan Takeoff"   v={ofp.fuel?.plan_takeoff} bold />
            <FuelLine k="Plan Ramp"      v={ofp.fuel?.plan_ramp} big />
          </div>
        </div>
        <div className="border border-border rounded-md p-4">
          <div className="overline mb-2">Weights ({ofp.weights?.units || "kgs"})</div>
          <div className="grid grid-cols-2 gap-y-1 text-sm font-mono">
            <FuelLine k="Pax count"  v={ofp.weights?.pax_count} />
            {paxDiffers && (
              <div className="col-span-2 text-[11px] text-amber-500 font-sans py-1" data-testid="fpls-ofp-pax-differs">
                Your assignment is {assignedPax} passengers. That is the figure filed on
                your PIREP; replan on SimBrief with {assignedPax} for matching fuel and weights.
              </div>
            )}
            <FuelLine k="Payload"    v={ofp.weights?.payload} bold />
            <FuelLine k="Est. ZFW"   v={ofp.weights?.est_zfw} />
            <FuelLine k="Est. TOW"   v={ofp.weights?.est_tow} big />
            <FuelLine k="Max TOW"    v={ofp.weights?.max_tow} muted />
            <FuelLine k="Est. LDW"   v={ofp.weights?.est_ldw} />
          </div>
        </div>
      </div>
      {ofp.alternates?.length > 0 && (
        <div className="border border-border rounded-md p-4">
          <div className="overline mb-2">Alternates</div>
          {ofp.alternates.map((a) => (
            <div key={a.icao} className="text-sm font-mono flex items-center justify-between py-1">
              <span><span className="text-primary font-bold">{a.icao}</span> · {a.name}</span>
              <span className="text-muted-foreground">{a.distance} nm</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---- Small helpers ----------------------------------------------------------
function Kv({ label, v, border }) {
  return (
    <div className={`px-4 py-3 bg-card ${border ? "border-l border-border" : ""}`}>
      <div className="overline mb-1">{label}</div>
      <div className="text-sm font-mono">{v ?? "—"}</div>
    </div>
  );
}
function Info({ label, v }) {
  return (
    <div className="border border-border rounded-md px-3 py-2">
      <div className="overline mb-0.5">{label}</div>
      <div className="text-sm font-mono">{v}</div>
    </div>
  );
}
function AirportPanel({ icon: Icon, label, a }) {
  return (
    <div className="border border-border rounded-md p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-primary" />
        <div>
          <div className="overline">{label}</div>
          <div className="text-sm font-mono font-bold">
            {a?.icao} · <span className="text-muted-foreground font-normal">{a?.name}</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 text-sm font-mono">
        <div><span className="overline block">Runway</span>{a?.runway || "—"}</div>
        <div><span className="overline block">Elevation</span>{a?.elevation ? `${a.elevation} ft` : "—"}</div>
        <div><span className="overline block">Trans</span>{a?.trans_alt || a?.trans_level || "—"}</div>
      </div>
    </div>
  );
}
function FuelLine({ k, v, bold, big, muted }) {
  return (
    <>
      <div className={`text-muted-foreground ${muted ? "opacity-70" : ""}`}>{k}</div>
      <div className={`text-right ${bold ? "font-bold" : ""} ${big ? "text-primary" : ""} ${muted ? "opacity-70" : ""}`}>
        {v != null && v !== "" ? Number(v).toLocaleString() : "—"}
      </div>
    </>
  );
}
function fmtSec(s) {
  const n = Number(s) || 0;
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  return `${h}h ${String(m).padStart(2,"0")}m`;
}
